import { AddressElement, PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js";
import * as React from "react";

import { IPaymentMethodUpdateResult } from "../../../libs";
import { paymentService } from "../../../services";
import * as Styled from "./MyPlan.styled";

interface IProps {
  onCancel: () => void;
  onComplete: (result: IPaymentMethodUpdateResult) => void;
  manualMode?: boolean;
  getErrorMessage: (error: unknown) => string;
}

export const PaymentMethodForm: React.FC<IProps> = ({
  onCancel,
  onComplete,
  manualMode,
  getErrorMessage,
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string>();

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!stripe || !elements || saving) return;

    setSaving(true);
    setError(undefined);
    try {
      const submitted = await elements.submit();
      if (submitted.error) throw submitted.error;

      const confirmation = await stripe.confirmSetup({
        elements,
        confirmParams: { return_url: window.location.href },
        redirect: "if_required",
      });
      if (confirmation.error) throw confirmation.error;
      if (!confirmation.setupIntent || confirmation.setupIntent.status !== "succeeded") {
        throw new Error("The card verification is still incomplete.");
      }

      const result = await paymentService.completePaymentMethodUpdate(confirmation.setupIntent.id);
      if (result.clientSecret && result.paymentStatus === "ACTION_REQUIRED") {
        const paymentConfirmation = await stripe.confirmPayment({
          clientSecret: result.clientSecret,
          confirmParams: { return_url: window.location.href },
          redirect: "if_required",
        });
        if (paymentConfirmation.error) throw paymentConfirmation.error;
      }
      onComplete(result);
    } catch (submitError) {
      setError(getErrorMessage(submitError));
      setSaving(false);
    }
  };

  return (
    <Styled.PaymentForm onSubmit={submit}>
      <Styled.ModalNote>
        {manualMode
          ? "Saving a card is optional and will not charge it or change your manual agreement. It can be used later if you choose automatic billing."
          : "Your new card becomes the default for future subscription charges. If an invoice is outstanding, Cemboo will retry it after the card is saved."}
      </Styled.ModalNote>
      <PaymentElement options={{ layout: "tabs" }} />
      <AddressElement options={{ mode: "billing" }} />
      {error && <Styled.ModalError>{error}</Styled.ModalError>}
      <Styled.ModalActions>
        <Styled.BillingButton type="button" $secondary onClick={onCancel} disabled={saving}>
          Cancel
        </Styled.BillingButton>
        <Styled.BillingButton type="submit" disabled={!stripe || saving}>
          {saving ? "Saving..." : "Save payment method"}
        </Styled.BillingButton>
      </Styled.ModalActions>
    </Styled.PaymentForm>
  );
};
